/**
 * This file interacts with a library that can have access and potentially expose our document data to third party vendors.
 * Have in mind any changes to existing interactions need to be carefully reviewed,
 * since we have sensitive patient data rendered in varied nodes throughout the pages.
 */
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import Config from "core/model/config";
import {
  activateLoggingTracking,
  activateRealUserMonitoring,
} from "core/model/utils/featureFlags";

// Single import site for Datadog logs SDK; other modules must use this export to avoid loading the SDK twice.
export { datadogLogs };

export type Logger = {
  debug(message: string, ...tags: Array<any>): void;
  error(message: string, ...tags: Array<any>): void;
  info(message: string, ...tags: Array<any>): void;
  warn(message: string, ...tags: Array<any>): void;
};

let datadogInit = false;

function startDatadog(appName: string) {
  if (datadogLogs) {
    datadogLogs.init({
      clientToken: "pub83c3331b43cb4994d28cb053017424ba",
      env: Config.environment,
      forwardConsoleLogs: [/*"debug",*/ "error", "info" /*"log"*/, "warn"],
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      site: "datadoghq.eu",
      version: process.env.BUILD_VERSION,
    });

    datadogLogs.setGlobalContext({
      app_name: appName,
      app_version: Config.version,
      environment: Config.environment,
      release: `${appName} - ${process.env.BUILD_VERSION}`,
      service: "frontend",
    });
  }
}

const originalConsole = console;

export const ConsoleLogger: Logger = {
  debug(message: string, tags: Record<string, any>) {
    if ("log" in originalConsole) {
      originalConsole.info(`${message} ${tags ? JSON.stringify(tags) : ""}`);
    }
  },
  error(message: string, tags: Record<string, any>) {
    if ("log" in originalConsole) {
      originalConsole.error(`${message} ${tags ? JSON.stringify(tags) : ""}`);
    }
  },
  info(message: string, tags: Record<string, any>) {
    if ("log" in originalConsole) {
      originalConsole.info(`${message} ${tags ? JSON.stringify(tags) : ""}`);
    }
  },
  warn(message: string, tags: Record<string, any>) {
    if ("log" in originalConsole) {
      originalConsole.warn(`${message} ${tags ? JSON.stringify(tags) : ""}`);
    }
  },
};

export class DatadogLogger {
  consoleLogger: Logger;

  constructor(appName: string) {
    this.consoleLogger = ConsoleLogger;

    if (!datadogInit) {
      startDatadog(appName);
      datadogInit = true;
      console.log(
        "Datadog Logger is configured for env",
        Config.environment,
        Config.version,
      );
    }
  }

  error(message: string, ...tags: Array<any>) {
    this.consoleLogger.error(message, tags);
  }

  info(message: string, ...tags: Array<any>) {
    this.consoleLogger.info(message, tags);
  }

  debug(message: string, ...tags: Array<any>) {
    this.consoleLogger.debug(message, tags);
  }
}

export const configureLogging = (appName: string) => {
  const logger = activateLoggingTracking
    ? new DatadogLogger(appName)
    : ConsoleLogger;

  // eslint-disable-next-line no-var
  var console = {
    assert: originalConsole.assert,
    clear: originalConsole.clear,
    count: originalConsole.count,
    debug: (message: string, context: Record<string, any>) =>
      logger.debug(message, context),
    dir: originalConsole.dir,
    dirxml: originalConsole.dirxml,
    error: (...args: unknown[]) => {
      if (activateRealUserMonitoring && args.length) {
        const [error, context] = args;
        const errorForRum =
          error instanceof Error ? error : new Error(String(error));
        const contextForRum = context instanceof Object ? context : undefined;
        datadogRum.addError(errorForRum, contextForRum);
      }
      return originalConsole.error(...args);
    },
    group: originalConsole.group,
    groupCollapsed: originalConsole.groupCollapsed,
    groupEnd: originalConsole.groupEnd,
    info: (message: string, context: Record<string, any>) =>
      logger.info(message, context),
    log: originalConsole.log,
    profile: originalConsole.profile,
    profileEnd: originalConsole.profileEnd,
    table: originalConsole.table,
    time: originalConsole.time,
    timeEnd: originalConsole.timeEnd,
    timeStamp: originalConsole.timeStamp,
    warn: (message: string, context: Record<string, any>) =>
      logger.info(message, context),
  };

  if (window) {
    // @ts-ignore
    window.console = console;
  }

  return logger;
};

export type LoggingType = {
  debug(eventName: string, context?: any): void;
  error(eventName: string, context?: any): void;
  info(eventName: string, context?: any): void;
};
